// @flow

import * as React from 'react';
import doThrottle from '@ta/common.throttle';

const MAX_NUMBER_OF_LOAD_MORES = 8; // this is the number of load mores that will be chained without an intersection observer

/**
 * Infinite loader hook.
 * @param contentRef scrolling content
 * @param containerRef scrolling container
 * @param loadMore loads next page
 * @param hasMore indicator if more data is available
 * @param enableInfiniteLoad enable the loader
 * @param threshold the number of pixels that the sentinel is away from the container that will trigger the callback
 * @param throttle the number of milliseconds between scroll events
 * @returns {[*, *]} - container reference, content reference
 */
const useInfiniteLoader = (
  contentRef: { current: null | HTMLElement },
  containerRef?: { current: null | HTMLElement },
  loadMore: () => Promise<mixed>,
  hasMore: boolean,
  enableInfiniteLoad: boolean,
  threshold: number,
  throttle?: number = 200,
): void => {
  const infiniteScrollEnabled = React.useRef(false);
  const [numberOfLoadMores, setNumberOfLoadMores] = React.useState(MAX_NUMBER_OF_LOAD_MORES);
  const [isLoading, setLoading] = React.useState<boolean>(false);

  // protect from non-memoized callbacks
  const latestLoadMoreRef = React.useRef(loadMore);
  React.useEffect(() => {
    latestLoadMoreRef.current = loadMore;
  }, [loadMore]);

  const isContentVisible = React.useCallback(() => {
    if (!contentRef.current) {
      return false;
    }
    const contentRect = contentRef.current.getBoundingClientRect();

    // Check against window
    if (!containerRef || !containerRef.current) {
      return (
        // eslint-disable-next-line no-restricted-globals
        window.innerHeight >= contentRect.bottom - threshold
      );
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    return containerRect.bottom >= contentRect.bottom - threshold;
  }, [containerRef, contentRef, threshold]);

  React.useEffect(() => {
    if (hasMore && isContentVisible() && numberOfLoadMores > 0 && isLoading) {
      latestLoadMoreRef
        .current()
        .catch(error => {
          console.error('infinite loader error: ', error);
          setLoading(false);
        })
        .then(() => {
          // this forces rerender and new effect to happen
          setNumberOfLoadMores(numberOfLoadMores - 1);
        });
      return;
    }
    if (isLoading) {
      setLoading(false);
    }
  }, [isLoading, numberOfLoadMores, hasMore, isContentVisible]);

  const checkToLoadMore = React.useCallback(() => {
    if (hasMore && infiniteScrollEnabled.current && isContentVisible() && !isLoading) {
      setNumberOfLoadMores(MAX_NUMBER_OF_LOAD_MORES);
      setLoading(true);
    }
  }, [hasMore, isContentVisible, isLoading]);

  const handleScroll = React.useCallback(
    throttle ? doThrottle(checkToLoadMore, throttle, false) : checkToLoadMore,
    [throttle],
  );

  React.useEffect(() => {
    const container = containerRef?.current;
    const handler = handleScroll;

    if (!container) {
      /* eslint-disable no-restricted-globals */
      window.addEventListener('scroll', handler);
      window.addEventListener('resize', handler);
      /* eslint-enable no-restricted-globals */
    } else {
      container.addEventListener('scroll', handler);
      container.addEventListener('resize', handler);
    }

    return () => {
      infiniteScrollEnabled.current = false;
      if (!container) {
        /* eslint-disable no-restricted-globals */
        window.removeEventListener('scroll', handler);
        window.removeEventListener('resize', handler);
        /* eslint-enable no-restricted-globals */
      } else if (container) {
        container.removeEventListener('scroll', handler);
        container.removeEventListener('resize', handler);
      }
    };
  }, [containerRef, handleScroll]);

  React.useEffect(() => {
    if (enableInfiniteLoad && !infiniteScrollEnabled.current) {
      infiniteScrollEnabled.current = true;
      checkToLoadMore(); // kickstart the process
    } else if (!enableInfiniteLoad && infiniteScrollEnabled.current) {
      infiniteScrollEnabled.current = false;
    }
  }, [checkToLoadMore, enableInfiniteLoad]);
};

export default useInfiniteLoader;
